import type { StepConfig, FormValues, FieldConfig, Card } from '../types';

interface FieldGroup {
  fields: FieldConfig[];
}

type SummaryItem = {
  label: string;
  value: string;
  fieldId: string;
  shortText?: string;
};

/**
 * Generates a summary of form data for display in cards
 * If card.includeFieldIds is provided, fields will be ordered according to that array
 */
export function getFormInstanceSummary(
  steps: StepConfig[],
  formData: FormValues,
  card?: Card
): SummaryItem[] {
  const summary: SummaryItem[] = [];
  steps.forEach((step) => {
    if (!step || !step.fieldGroups) return;

    step.fieldGroups.forEach((group: FieldGroup) => {
      group.fields.forEach((field) => {
        // If includeFieldIds is specified, only include those fields
        if (card?.includeFieldIds && !card.includeFieldIds.includes(field.id)) {
          return;
        }

        // If excludeFieldIds is specified, exclude those fields
        if (card?.excludeFieldIds && card.excludeFieldIds.includes(field.id)) {
          return;
        }

        const value = formData[field.id];
        if (value !== undefined && value !== null && value !== '') {
          let displayValue = String(value);
          let shortText: string | undefined;
          // Handle select/radio options - show label if available
          if ((field.type === 'select' || field.type === 'radio') && field.options) {
            const option = field.options.find((opt) => opt.value === String(value));
            if (option) {
              displayValue = option.label;
              shortText =
                typeof (option as { shortText?: unknown }).shortText === 'string'
                  ? ((option as { shortText?: string }).shortText ?? undefined)
                  : undefined;
            }
          }
          if (field.id === 'executorChoice') {
            field.label = '';
          }
          if (field.id === 'hasDifferentNameAssets' && value === 'Yes') {
            displayValue = displayValue === 'Yes' ? 'Yes - ' : '';
          }
          if (field.id === 'estateDisabilityBeneficiaryPreference' && value === 'Yes') {
            displayValue = displayValue === 'Yes' ? 'Yes - ' : '';
          }
          summary.push({ label: field.label, value: displayValue, fieldId: field.id, shortText });
        }
      });
    });
  });

  // Personal details card: show "UK" as residency when ispermanentUKResident is Yes
  if (
    card?.id === 'card-personal-details' &&
    formData.ispermanentUKResident === 'Yes' &&
    !summary.some((item) => item.fieldId === 'countryOfPermanentResidence')
  ) {
    summary.push({
      label: 'Residency',
      value: 'UK',
      fieldId: 'countryOfPermanentResidence',
    });
  }

  // If includeFieldIds is specified, reorder summary to match that order
  if (card?.includeFieldIds && card.includeFieldIds.length > 0) {
    const orderedSummary: SummaryItem[] = [];
    const summaryMap = new Map(summary.map((item) => [item.fieldId, item]));

    // Add fields in the order specified by includeFieldIds
    card.includeFieldIds.forEach((fieldId) => {
      const item = summaryMap.get(fieldId);
      if (item) {
        orderedSummary.push(item);
      }
    });

    // Add any remaining fields that weren't in includeFieldIds (shouldn't happen, but for safety)
    summary.forEach((item) => {
      if (!card?.includeFieldIds?.includes(item.fieldId)) {
        orderedSummary.push(item);
      }
    });
    return orderedSummary;
  }

  return summary;
}
